import React, { useId, useLayoutEffect, useState } from 'react';
import { getPlayerEffect } from '../services/PlayerEffects';
import './PlayerEffectsLayer.scss';

export const PLAYER_EFFECT_PREPARE_MS = 1000;
export const PLAYER_EFFECT_FLIGHT_MS = 900;
export const PLAYER_EFFECT_IMPACT_MS = PLAYER_EFFECT_PREPARE_MS + PLAYER_EFFECT_FLIGHT_MS;
export const PLAYER_EFFECT_TOTAL_MS = 5200;

const findAvatar = playerId => Array.from(document.querySelectorAll('[data-room-player-id]'))
  .find(element => element.dataset.roomPlayerId === String(playerId));

const point = rect => ({ x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 });

const curvedPath = (from, to) => {
  const deltaX = to.x - from.x;
  const deltaY = to.y - from.y;
  const distance = Math.max(1, Math.hypot(deltaX, deltaY));
  const lift = Math.min(95, Math.max(45, distance * 0.2));
  const sideCurve = Math.min(28, Math.max(12, distance * 0.055));
  let normalX = -deltaY / distance;
  let normalY = deltaX / distance;
  if (normalY > 0) { normalX *= -1; normalY *= -1; }

  const keepForward = value => {
    if (Math.abs(deltaX) < 32) return value;
    const lower = Math.min(from.x, to.x) + Math.abs(deltaX) * 0.08;
    const upper = Math.max(from.x, to.x) - Math.abs(deltaX) * 0.08;
    return Math.min(upper, Math.max(lower, value));
  };
  const firstControlX = keepForward(from.x + deltaX * 0.3 + normalX * sideCurve);
  const secondControlX = keepForward(from.x + deltaX * 0.7 + normalX * sideCurve);
  const firstControlY = from.y + deltaY * 0.3 - lift + normalY * sideCurve;
  const secondControlY = from.y + deltaY * 0.7 - lift * 0.72 + normalY * sideCurve;
  return `M ${from.x} ${from.y} C ${firstControlX} ${firstControlY} ${secondControlX} ${secondControlY} ${to.x} ${to.y}`;
};

function PlayerEffectAnimation({ event }) {
  const [path, setPath] = useState(null);
  const effect = getPlayerEffect(event.effectId);
  const rawId = useId();
  const gradientId = `player-effect-trail-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  useLayoutEffect(() => {
    const sender = findAvatar(event.senderPlayerId);
    const target = findAvatar(event.targetPlayerId);
    if (!sender || !target) return undefined;
    const from = point(sender.getBoundingClientRect());
    const to = point(target.getBoundingClientRect());
    setPath({
      fromX: from.x,
      fromY: from.y,
      targetX: to.x,
      targetY: to.y,
      d: curvedPath(from, to),
    });

    const senderPanel = sender.closest('.player-panel');
    senderPanel?.animate?.([
      { transform: 'scale(1) rotate(0)' },
      { transform: 'scale(1.035) rotate(-1deg)', offset: 0.35 },
      { transform: 'scale(1.035) rotate(1deg)', offset: 0.7 },
      { transform: 'scale(1) rotate(0)' },
    ], { duration: PLAYER_EFFECT_PREPARE_MS, easing: 'ease-in-out' });

    const targetPanel = target.closest('.player-panel');
    const isKind = effect?.category === 'kind';
    targetPanel?.animate?.(isKind
      ? [{ transform: 'scale(1)' }, { transform: 'scale(1.08)' }, { transform: 'scale(1)' }]
      : [{ transform: 'translateX(0)' }, { transform: 'translateX(-5px)' }, { transform: 'translateX(5px)' }, { transform: 'translateX(0)' }],
    { delay: PLAYER_EFFECT_IMPACT_MS, duration: 650, iterations: isKind ? 2 : 3 });
    return undefined;
  }, [effect?.category, event]);

  if (!effect || !path) return null;
  const hasCrushImpact = effect.id === 'tomato' || effect.id === 'egg';
  const projectileSize = Math.min(74, Math.max(44, window.innerWidth * 0.08));
  const style = {
    '--effect-from-x': `${path.fromX}px`, '--effect-from-y': `${path.fromY}px`,
    '--effect-target-x': `${path.targetX}px`, '--effect-target-y': `${path.targetY}px`,
    '--effect-prepare-ms': `${PLAYER_EFFECT_PREPARE_MS}ms`,
    '--effect-flight-ms': `${PLAYER_EFFECT_FLIGHT_MS}ms`,
    '--effect-impact-delay': `${PLAYER_EFFECT_IMPACT_MS}ms`,
  };

  return <div className={`player-effect-animation effect-${effect.id}`} style={style} aria-hidden="true">
    <span className="player-effect-mystery-box" data-testid="player-effect-mystery"><span>?</span></span>
    <svg className="player-effect-flight-layer" width="100%" height="100%" viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`} preserveAspectRatio="none">
      <defs>
        <linearGradient id={gradientId} gradientUnits="userSpaceOnUse" x1={path.fromX} y1={path.fromY} x2={path.targetX} y2={path.targetY}>
          <stop offset="0" className="player-effect-trail-start" />
          <stop offset="1" className="player-effect-trail-end" />
        </linearGradient>
      </defs>
      <path className="player-effect-trail-glow" pathLength="1" d={path.d} />
      <path className="player-effect-trail" data-testid="player-effect-trail" pathLength="1" d={path.d} stroke={`url(#${gradientId})`} />
      <g className="player-effect-projectile-motion">
        <animateMotion data-testid="player-effect-motion" path={path.d} begin={`${PLAYER_EFFECT_PREPARE_MS}ms`} dur={`${PLAYER_EFFECT_FLIGHT_MS}ms`} fill="freeze" calcMode="paced" />
        <image className="player-effect-projectile-image" data-testid="player-effect-projectile" href={effect.image} x={-projectileSize / 2} y={-projectileSize / 2}
          width={projectileSize} height={projectileSize} preserveAspectRatio="xMidYMid meet" />
      </g>
    </svg>
    <span className="player-effect-impact"><img src={effect.image} alt="" /></span>
    {hasCrushImpact && <span className="player-effect-splat">
      <span className="player-effect-splat-core" />
      {Array.from({ length: 10 }, (_, index) => <i key={index} />)}
    </span>}
  </div>;
}

export default function PlayerEffectsLayer({ effects }) {
  return <div className="player-effects-layer" aria-hidden="true">
    {effects.map(effect => <PlayerEffectAnimation key={effect.eventId} event={effect} />)}
  </div>;
}
